// SkuUnitCodesCard — ユニット図番 (unit-code) master CRUD.
// Extracted from SkuMasterSettings.js in iter_145.
// iter_148 · Visual polish: card grid, colored code chips, sub_codes as
// inline chips, count badge in header, click-row-to-edit.
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Trash, Plus, PencilSimple, MagnifyingGlass, Hash, ClockCounterClockwise } from '@phosphor-icons/react';
import { formatDate } from '@/utils/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SkuUnitCodesCard() {
  const [units, setUnits] = useState([]);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('unit_no');    // 'unit_no' | 'code'
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ unit_no: 0, code: '', alt_code: '', sub_codes: [], name: '', sort_order: 0, active: true });
  // iter_150 · Drill-down: unit card → issued-SKU history dialog
  const [historyFor, setHistoryFor] = useState(null); // unit doc
  const [history, setHistory] = useState({ loading: false, list: [] });

  const openHistory = async (u) => {
    setHistoryFor(u);
    setHistory({ loading: true, list: [] });
    try {
      const r = await axios.get(`${API}/sku/issued`, { params: { unit_code: u.code, limit: 200 }, withCredentials: true });
      setHistory({ loading: false, list: Array.isArray(r.data) ? r.data : [] });
    } catch (e) {
      setHistory({ loading: false, list: [] });
      toast.error(e?.response?.data?.detail || '発番履歴の取得に失敗しました');
    }
  };

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/sku/unit-codes`, { withCredentials: true });
    setUnits(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    let out = s
      ? units.filter((u) =>
          String(u.unit_no).includes(s)
          || u.code.toLowerCase().includes(s)
          || (u.alt_code || '').toLowerCase().includes(s)
          || (u.name || '').toLowerCase().includes(s))
      : [...units];
    out.sort((a, b) => sort === 'unit_no'
      ? (a.unit_no || 0) - (b.unit_no || 0)
      : (a.code || '').localeCompare(b.code || ''));
    return out;
  }, [units, q, sort]);

  const openNew = () => { setEditing('new'); setForm({ unit_no: 0, code: '', alt_code: '', sub_codes: [], name: '', sort_order: units.length + 1, active: true }); };
  const openEdit = (u) => { setEditing(u.id); setForm({ ...u, sub_codes: u.sub_codes || [] }); };

  const save = async () => {
    try {
      const payload = { ...form, alt_code: form.alt_code || null };
      if (editing === 'new') await axios.post(`${API}/sku/unit-codes`, payload, { withCredentials: true });
      else await axios.put(`${API}/sku/unit-codes/${editing}`, payload, { withCredentials: true });
      toast.success('保存しました');
      setEditing(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存失敗');
    }
  };
  const remove = async (id) => {
    if (!window.confirm('ユニットコードを削除しますか？')) return;
    await axios.delete(`${API}/sku/unit-codes/${id}`, { withCredentials: true });
    toast.success('削除しました'); load();
  };

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h3 className="text-sm font-bold text-zinc-700 flex items-center gap-1.5">
            <Hash size={16} weight="duotone" className="text-cyan-600" />
            ユニット図番 <Badge variant="secondary" className="ml-1 bg-cyan-100 text-cyan-800 border-cyan-200">{filtered.length}/{units.length}</Badge>
          </h3>
          <p className="text-[11px] text-zinc-500 mt-0.5">ユニット番号と 3文字の主コード + 補助コードで図番を管理します。</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="flex gap-1 rounded-lg bg-zinc-100 p-1">
            <button
              onClick={() => setSort('unit_no')}
              className={`px-2 h-7 rounded text-[11px] font-semibold ${sort === 'unit_no' ? 'bg-white shadow-sm text-cyan-700' : 'text-zinc-500 hover:text-zinc-700'}`}
              data-testid="sku-unit-sort-no"
            >№順</button>
            <button
              onClick={() => setSort('code')}
              className={`px-2 h-7 rounded text-[11px] font-semibold ${sort === 'code' ? 'bg-white shadow-sm text-cyan-700' : 'text-zinc-500 hover:text-zinc-700'}`}
              data-testid="sku-unit-sort-code"
            >コード順</button>
          </div>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="番号 / コード / 名称で検索…" className="pl-7 h-9 w-56" data-testid="sku-unit-search" />
          </div>
          <Button size="sm" onClick={openNew} data-testid="sku-unit-new" className="bg-teal-700 hover:bg-teal-800">
            <Plus size={14} /><span className="ml-1">新規</span>
          </Button>
        </div>
      </div>
      {filtered.length === 0 ? (
        <div className="text-center py-10 text-zinc-400 text-sm">
          {q ? `「${q}」に一致するユニットはありません` : 'ユニット図番はまだ登録されていません'}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[500px] overflow-y-auto pr-1" data-testid="sku-unit-grid">
          {filtered.map((u) => (
            <div
              key={u.id}
              onClick={() => openEdit(u)}
              data-testid={`sku-unit-row-${u.unit_no}`}
              className="group rounded-xl border border-zinc-200 bg-white p-3 flex items-start gap-2.5 hover:border-cyan-400 hover:shadow-sm transition-all cursor-pointer"
            >
              <div className="flex-shrink-0 flex flex-col items-center">
                <div className="w-11 h-11 rounded-lg bg-zinc-50 border border-cyan-200 flex items-center justify-center font-mono font-bold text-cyan-800 text-sm">
                  {u.code}
                </div>
                <span className="text-[10px] font-mono text-zinc-400 mt-0.5">№ {u.unit_no}</span>
              </div>
              <div className="flex-1 min-w-0">
                {u.name && <div className="font-semibold text-sm text-zinc-800 truncate">{u.name}</div>}
                <div className="text-[11px] text-zinc-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                  {u.alt_code && (
                    <span className="font-mono px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600" title="別コード">別: {u.alt_code}</span>
                  )}
                  {(u.sub_codes || []).map((c) => (
                    <span key={c} className="font-mono px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-700 border border-cyan-100">{c}</span>
                  ))}
                  {!u.alt_code && (u.sub_codes || []).length === 0 && (
                    <span className="text-zinc-300">補助コードなし</span>
                  )}
                </div>
              </div>
              <div className="flex-shrink-0 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openHistory(u); }} className="h-7 w-7 p-0 text-cyan-600 hover:bg-cyan-50" title="このユニットの発番履歴" data-testid={`sku-unit-history-${u.unit_no}`}><ClockCounterClockwise size={13} /></Button>
                <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openEdit(u); }} className="h-7 w-7 p-0"><PencilSimple size={13} /></Button>
                <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); remove(u.id); }} className="h-7 w-7 p-0 text-rose-600 hover:bg-rose-50"><Trash size={13} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="sku-unit-dialog">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'ユニット図番を追加' : 'ユニット図番を編集'}</DialogTitle>
            <DialogDescription>ユニット番号（例: 4）と 3文字コード（例: CAX）を指定します。</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">ユニット番号</Label>
                <Input type="number" value={form.unit_no} onChange={(e) => setForm({ ...form, unit_no: parseInt(e.target.value) || 0 })}
                       className="h-10 mt-1" disabled={editing !== 'new'} />
              </div>
              <div>
                <Label className="text-xs">主コード (3文字)</Label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().slice(0, 3) })}
                       maxLength={3} className="h-10 mt-1 font-mono uppercase" />
              </div>
            </div>
            <div>
              <Label className="text-xs">別コード (任意)</Label>
              <Input value={form.alt_code || ''} onChange={(e) => setForm({ ...form, alt_code: e.target.value.toUpperCase().slice(0, 3) })}
                     maxLength={3} className="h-10 mt-1 font-mono uppercase" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>キャンセル</Button>
            <Button onClick={save} data-testid="sku-unit-save" className="bg-teal-700 hover:bg-teal-800">保存</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!historyFor} onOpenChange={(v) => !v && setHistoryFor(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="sku-unit-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClockCounterClockwise size={18} className="text-cyan-600" />
              発番履歴 — {historyFor?.code}{historyFor?.name ? `（${historyFor.name}）` : ''}
            </DialogTitle>
            <DialogDescription>このユニット図番を使って発番されたSKUの一覧です（最新200件）。</DialogDescription>
          </DialogHeader>
          {history.loading ? (
            <p className="py-8 text-center text-xs text-zinc-400">読み込み中…</p>
          ) : history.list.length === 0 ? (
            <p className="py-8 text-center text-sm text-zinc-400" data-testid="sku-unit-history-empty">このユニットでの発番はまだありません</p>
          ) : (
            <div className="rounded-lg border border-zinc-200 overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50/70 text-left text-[10px] text-zinc-400">
                    <th className="px-3 py-2 font-medium">SKU</th>
                    <th className="px-2 py-2 font-medium">品番ルール</th>
                    <th className="px-2 py-2 font-medium">発番日</th>
                    <th className="px-3 py-2 font-medium">発番者</th>
                  </tr>
                </thead>
                <tbody>
                  {history.list.map((d) => (
                    <tr key={d.id} className="border-b border-zinc-50 last:border-0" data-testid={`sku-unit-history-row-${d.id}`}>
                      <td className="px-3 py-1.5 font-mono font-semibold text-cyan-800">{d.sku}</td>
                      <td className="px-2 py-1.5 text-zinc-600">{d.template_name || d.template_code}</td>
                      <td className="px-2 py-1.5 tabular-nums text-zinc-500">{d.issued_at ? formatDate(d.issued_at) : '—'}</td>
                      <td className="px-3 py-1.5 text-zinc-500">{d.issued_by || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
